import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  selectedVariantId,
  isAvailable,
  selectedQuantity,
  productData,
  shipsBy,
  selectedVariant,
} from "../store/product-store";
import {
  isCartLoading,
  getLimitStatus,
  cartLimitError,
  LimitStatus,
} from "../store/cart-store";
import { startModalChain } from "../store/modal-store";
import { isExtendedOfferEnabled } from "../store/extended-offer-store";
import {
  isVoucherProductTags,
  shouldMergeVouchers,
  voucherError,
} from "../store/voucher-store";

type ButtonState = "add-to-cart" | "extended-offer" | "limit-reached";

@customElement("add-to-cart")
export class AddToCart extends LitElement {
  @state() private _available = false;
  @state() private _loading = false;
  @state() private _variantId: number | null = null;
  @state() private _limitReached = false;
  @state() private _extendedOfferEnabled = false;
  @state() private _voucherError = false;
  private _quantity = 1;
  private _productId: number | null = null;
  private _vendor = "";

  constructor() {
    super();
    effect(() => {
      this._available = isAvailable.value;
      this._loading = isCartLoading.value;
      this._variantId = selectedVariantId.value;
      this._quantity = selectedQuantity.value;
      this._extendedOfferEnabled = Boolean(isExtendedOfferEnabled.value);
      this._voucherError = voucherError.value;

      const pd = productData.value;
      if (pd) {
        this._productId = pd.id;
        this._vendor = pd.vendor ?? "";
        const status = getLimitStatus(pd.id, pd.vendor ?? "");
        this._limitReached = status.reached;
      }

    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private get _buttonState(): ButtonState {
    if (!this._available && this._extendedOfferEnabled) return "extended-offer";
    if (this._limitReached) return "limit-reached";
    return "add-to-cart";
  }

  private async _handleClick(e: Event) {
    e.preventDefault();
    if (!this._variantId || this._loading) return;

    const state = this._buttonState;

    // If extended offer, open the modal instead of adding to cart
    if (state === "extended-offer") {
      const modal = (window as any).extendedOfferModal;
      if (modal) {
        modal.open();
      }
      return;
    }

    if (this._limitReached) return;

    const button = e.target as HTMLButtonElement;

    // Build customization properties
    const properties: Record<string, any> = {};
    if (shipsBy.value) {
      properties["_ships-by"] = shipsBy.value;
    }

    // Start modal chain (if no modals show, it will add to cart directly)
    // Cart opens automatically after the real API call succeeds
    const variant = selectedVariant.value;

    let imageUrl = variant?.featured_image?.src ||
                   variant?.image ||
                   productData.value?.featured_image ||
                   "";

    // Ensure protocol is included for protocol-relative URLs
    if (imageUrl.startsWith("//")) {
      imageUrl = "https:" + imageUrl;
    }

    // Remove query string (like ?v=...) to match Shopify cart API format
    // This allows cart-item-row to properly apply _180x transformation
    imageUrl = imageUrl.split('?')[0];

    const tags = productData.value?.tags as string[] | undefined;
    const isVoucher = isVoucherProductTags(tags);

    await startModalChain({
      productId: this._productId || undefined,
      variantId: this._variantId,
      quantity: this._quantity,
      productTitle: productData.value?.title,
      variantTitle: selectedVariant.value?.title,
      customization: properties,
      triggerButton: button,
      vendor: this._vendor,
      imageUrl: imageUrl,
      source: e.isTrusted ? "main-button" : "sticky-add-to-cart",
      isVoucher,
      variantSku: isVoucher ? selectedVariant.value?.sku : undefined,
      mergeVouchers: isVoucher ? shouldMergeVouchers(tags) : undefined,
    });
  }

  private _renderSpinner() {
    return html`
      <div class="flex items-center justify-center w-full">
        <svg
          aria-hidden="true"
          focusable="false"
          role="presentation"
          class="animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="rgba(255,255,255,0.25)"
            stroke-width="2.5"
          ></circle>
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="rgba(255,255,255,0.9)"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-dasharray="62.83"
            stroke-dashoffset="47"
          ></circle>
        </svg>
      </div>
    `;
  }

  render() {
    const state = this._buttonState;

    const config: Record<
      ButtonState,
      { label: string; disabled: boolean; styleClass: string; styleOverride: string }
    > = {
      "add-to-cart": {
        label: "Add to cart",
        disabled: false,
        styleClass: "btn",
        styleOverride: "",
      },
      "extended-offer": {
        label: "Notify Me",
        disabled: false,
        styleClass: "btn",
        styleOverride:
          "bg-[var(--color-secondary-500)] hover:bg-[var(--color-secondary-500)] text-white",
      },
      "limit-reached": {
        label: "Add to cart",
        disabled: false,
        styleClass: "btn",
        styleOverride: "",
      },
    };

    const { label, styleClass, styleOverride } = config[state];
    const isDisabled = state !== "extended-offer" && this._limitReached || this._loading;

    const buttonStyles =
      "btn w-full flex items-center justify-center text-sm font-bold h-[46px] px-[25px] rounded-t-none";
    return html`
      <button
        type="button"
        name="add"
        class="product-form__submit button w-full ${styleClass} ${buttonStyles} ${styleOverride} ${isDisabled
          ? "opacity-50 cursor-not-allowed"
          : ""}"
        ?disabled=${isDisabled}
        @click=${this._handleClick}
      >
        ${this._loading ? this._renderSpinner() : html`<span>${label}</span>`}
      </button>
      ${this._voucherError
        ? html`<p
            class="text-sm text-[var(--color-error-500)] mt-2"
            role="alert"
          >
            Couldn't be added to the cart, please try again
          </p>`
        : ""}
    `;
  }
}

@customElement("cart-limit-notice")
export class CartLimitNotice extends LitElement {
  @state() private _limitError: LimitStatus | null = null;
  @state() private _proactiveStatus: LimitStatus | null = null;
  @state() private _dismissed = false;

  constructor() {
    super();
    effect(() => {
      this._limitError = cartLimitError.value;
    });
    effect(() => {
      const pd = productData.value;
      if (!pd) {
        this._proactiveStatus = null;
        return;
      }
      const status = getLimitStatus(pd.id, pd.vendor ?? "");
      // Re-show if the status changes (e.g. variant switch) while dismissed
      const nextStatus = status.reached ? status : null;
      if (nextStatus?.type !== this._proactiveStatus?.type || nextStatus?.limit !== this._proactiveStatus?.limit) {
        this._dismissed = false;
      }
      this._proactiveStatus = nextStatus;
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  render() {
    const notice = this._limitError ?? (!this._dismissed ? this._proactiveStatus : null);
    if (!notice) return html``;
    const limitType = notice.type === "brand" ? "brand" : "product";
    return html`
      <div
        class="flex items-center justify-between bg-[var(--color-info-100)] rounded-sm p-2 md:p-4 mt-4"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <p
            class="text-sm md:text-base font-bold text-[var(--color-neutral-950)] leading-snug"
          >
            Product Limit Reached
          </p>
          <p
            class="text-xs md:text-sm font-light text-[var(--color-neutral-700)] leading-normal"
          >
            You can only buy ${notice.limit ?? ""} of this
            ${limitType} per order
          </p>
        </div>
        <button
          type="button"
          class="flex-shrink-0 ml-4 p-1 bg-transparent border-none cursor-pointer text-[var(--color-neutral-400)] hover:text-[var(--color-neutral-600)] transition-colors"
          aria-label="Close"
          @click=${() => {
            cartLimitError.value = null;
            this._dismissed = true;
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            role="presentation"
            class="icon icon-close"
            fill="none"
            width="15"
            height="15"
            viewBox="0 0 18 17"
          >
            <path
              d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"
              fill="currentColor"
            ></path>
          </svg>
        </button>
      </div>
    `;
  }
}
